import React, { useCallback, useEffect, useState } from "react";
import { IconButton } from "../../../template/Buttons/IconButton/IconButton";
import colors from "../../colors";
import { ClientShowerBar } from "../ClientShowerBar/ClientShowerBar";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
	dataClientsApi,
	getCompaniesData,
	updateDB,
} from "../../../apis/data.api";
import { newsApi } from "../../../apis/news.api";
import { contactsApi } from "../../../apis/contact.api";
import {
	Avatar,
	Box,
	Grid,
	Skeleton,
	TextField,
	Typography,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Toaster, toast } from "sonner";
import { RootState } from "../../../app/store";

interface ClientProps {
	CompanySelections: (clientsName: string, folder: string) => void;
	UserSelection: React.Dispatch<React.SetStateAction<boolean>>;
}
type CompanyItem = {
	_id: string;
	NombreCliente: string[];
	Carpetas: string[];
};
type CompaniesBrief = CompanyItem[];
const stringAvatar = (name: string) => {
	let initials = name[0] ? name[0].toUpperCase() : "";
	initials += name[1] ? name[1].toUpperCase() : "";

	return {
		sx: {
			bgcolor: "#d9d9d9",
		},
		children: initials,
	};
};
export const ClientsBar: React.FC<ClientProps> = ({
	CompanySelections,
	UserSelection,
}) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const userInfo = location.state?.userInfo;

	const token = useSelector((state: RootState) => state.auth.token);
	const [activeClient, setActiveClient] = useState<string | null>(null);
	const [userStatus, setUserStatus] = useState<boolean>(true);

	const isAdmin = userInfo?.role[0].toUpperCase() === "ADMINISTRADOR";
	const [filter, setFilter] = useState<string>("");

	const [companiesData, setCompaniesData] = useState<CompaniesBrief>([]);

	const [isLoading, setIsLoading] = useState<boolean>(false);

	const getCompaniesInfo = useCallback(async () => {
		try {
			setIsLoading(true);
			const response = await getCompaniesData(token as string);
			const updatedResponse = response.map((company: any) => {
				return {
					...company,
					Carpetas: Array.from(new Set(company.Carpetas)),
				};
			});
			setCompaniesData(updatedResponse);
		} catch (error) {
		} finally {
			setIsLoading(false);
		}
	}, [token]);

	useEffect(() => {
		getCompaniesInfo();
	}, [getCompaniesInfo]);

	const handleUpdate = async () => {
		try {
			await updateDB(token as string);
			dispatch(dataClientsApi.util.resetApiState());
			dispatch(newsApi.util.resetApiState());
			dispatch(contactsApi.util.resetApiState());
			toast.success("BASE DE DATOS ACTUALIZADA");
		} catch (error) {
			toast.error("ERROR AL ACTUALIZAR BASE DE DATOS");
		}
	};

	const handleClientSelection = async (clientName: string, folder: string) => {
		setActiveClient(clientName);
		CompanySelections(clientName, folder);
		if (activeClient === clientName) {
			setActiveClient(null);
		} else {
			setActiveClient(clientName);
		}
	};

	const handleUser = () => {
		const newUserStatus = !userStatus;
		setUserStatus(newUserStatus);
		UserSelection((prevState: boolean) => !prevState);
	};
	const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFilter(event.target.value);
	};

	const handleSelectionChange = (newActiveClient: string) => {
		if (activeClient !== newActiveClient) {
			setActiveClient(newActiveClient);
		}
	};
	const handleLogout = () => {
		dispatch(dataClientsApi.util.resetApiState());
		dispatch(newsApi.util.resetApiState());
		dispatch(contactsApi.util.resetApiState());
		navigate("/");
	};
	type FilteredCompany = {
		_id: string;
		NombreCliente: string[];
		Carpetas: string[];
	};

	let filteredData: (CompanyItem | FilteredCompany)[] = [];

	if (filter) {
		filteredData = (companiesData || []).flatMap((item) => {
			const filterLower = filter.toLowerCase();

			if (item._id.toLowerCase().includes(filterLower)) {
				return [item];
			}

			const nameMatch = item.NombreCliente.some((name) =>
				name.toLowerCase().includes(filterLower)
			);
			if (nameMatch) {
				return [item];
			}

			const matchingFolders = item.Carpetas.filter((Carpeta) =>
				Carpeta.toLowerCase().includes(filterLower)
			);

			if (matchingFolders.length > 0) {
				return [
					{
						_id: item._id,
						NombreCliente: item.NombreCliente,
						Carpetas: matchingFolders,
					},
				];
			} else {
				return [];
			}
		});
	} else {
		filteredData = companiesData;
	}

	return (
		<>
			<Toaster position="bottom-right" richColors />
			<Box
				sx={{
					width: "100%",
					height: "100%",
					maxHeight: "100vh",
					backgroundColor: colors.primaryGreen,
					margin: "auto",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Grid
					container
					sx={{
						width: "100%",
						backgroundColor: colors.primaryGreen,
						display: "flex",
						margin: "auto",
					}}
				>
					{/* User Avatar */}
					<Grid
						item
						xs={12}
						sx={{
							width: "80%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							margin: "1rem",
						}}
					>
						{userInfo.username ? (
							<Avatar
								{...stringAvatar(userInfo?.username ?? "")}
								sx={{ width: 60, height: 60 }}
							/>
						) : (
							<Skeleton variant="circular" height="2.5rem" width="2.5rem" />
						)}
					</Grid>

					{/* User Role Button */}
					<Grid
						item
						xs={12}
						sx={{
							margin: "1rem 1.5rem",
							height: "2rem",
							width: "100%",
							display: "flex",
							justifyContent: "center",
						}}
					>
						<IconButton
							backgroundcolorprop={colors.white}
							IconNum={0}
							buttonLabel={`${userInfo?.role[0].toUpperCase()}`}
							textcolor={colors.primaryGreen}
							hoverbackgroundcolor={colors.green}
							hovertextcolor={colors.white}
							hoverbordercolor={colors.green}
							OnClick={() => handleUser()}
							fontSize="1rem"
						/>
					</Grid>

					{/* Search Filter */}
					<Grid
						item
						xs={12}
						sx={{
							margin: "1rem 1.5rem",
							height: "1.5rem",
							width: "100%",
							maxWidth: "80%",
						}}
					>
						<TextField
							size="medium"
							placeholder="Filtrar por Nombre o Carpeta"
							value={filter}
							onChange={handleFilterChange}
							InputProps={{
								endAdornment: <FilterAltIcon sx={{ color: colors.white }} />,
							}}
							sx={{
								width: "100%",
								"& .MuiInputBase-root": {
									height: "2.5rem",
									backgroundColor: "rgba(255, 255, 255, 0.05)",
									borderRadius: "8px",
								},
								"& .MuiInputBase-input": {
									color: colors.white,
									"&::placeholder": {
										color: "rgba(255, 255, 255, 0.7)",
										opacity: 1,
									},
								},
								"& .MuiOutlinedInput-root": {
									"& fieldset": {
										borderColor: "rgba(255, 255, 255, 0.3)",
										borderWidth: 1,
									},
									"&:hover fieldset": {
										borderColor: colors.green,
										borderWidth: 2,
									},
									"&.Mui-focused fieldset": {
										borderColor: colors.green,
										borderWidth: 2,
									},
								},
							}}
						/>
					</Grid>

					{/* Companies List */}
					<Grid
						item
						xs={12}
						sx={{
							margin: "2rem auto",
							padding: 0,
							width: "100%",
							minWidth: "18rem",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							marginTop: "1rem",
							marginBottom: "2rem",
							justifyContent: "center",
						}}
					>
						<Box
							sx={{
								height: "20rem",
								minHeight: "10rem",
								maxHeight: "20rem",
								maxWidth: "80%",
								minWidth: "80%",
								backgroundColor: "rgba(255, 255, 255, 0.05)",
								borderRadius: "8px",
								padding: "1rem",
								display: "flex",
								flexDirection: "column",
							}}
						>
							<Typography
								variant="h1"
								sx={{
									margin: 0,
									fontFamily: "Montserrat",
									fontStyle: "normal",
									fontWeight: 800,
									fontSize: "1rem",
									lineHeight: "1.375rem",
									color: colors.green,
								}}
							>
								Empresas
							</Typography>
							<Box
								sx={{
									width: "100%",
									overflowY: "auto",
									minHeight: "10rem",
									maxHeight: "20rem",

									// Scrollbar customization (webkit-based)
									"&::-webkit-scrollbar": {
										width: "8px",
									},
									"&::-webkit-scrollbar-track": {
										backgroundColor: colors.primaryGreen,
									},
									"&::-webkit-scrollbar-thumb": {
										backgroundColor: colors.white,
									},
									"&::-webkit-scrollbar-button": {
										display: "none",
									},
								}}
							>
								{!companiesData || isLoading ? (
									<Box sx={{ width: "100%" }}>
										{[...Array(12)].map((_, index) => (
											<Box
												key={index}
												sx={{
													padding: "0.5rem 0",
													borderBottom: "0.3px solid rgba(255,255,255,0.1)",
												}}
											>
												<Skeleton
													variant="text"
													sx={{
														color: colors.white,
														height: "2.5rem",
														transform: "none",
													}}
												/>
											</Box>
										))}
									</Box>
								) : (
									filteredData.map((Company, index) => (
										<ClientShowerBar
											key={`${Company._id}-${index}`}
											ClientName={Company.NombreCliente[0]}
											Folders={(Company as CompanyItem).Carpetas || []}
											onClientNameClick={handleClientSelection}
											onSelectionChange={() =>
												handleSelectionChange(Company.NombreCliente[0])
											}
											isActive={Company._id === activeClient}
											index={index}
											Company_id={Company._id}
										/>
									))
								)}
							</Box>
						</Box>
					</Grid>

					{/* Action Buttons */}
					<Box
						sx={{
							width: "80%",
							display: "flex",
							flexDirection: "column",
							gap: "1rem",
							margin: "0 auto",
							marginBottom: "1.5rem",
							alignItems: "center",
						}}
					>
						{isAdmin && (
							<Grid
								item
								xs={12}
								sx={{
									width: "100%",
									display: "flex",
									justifyContent: "center",
									marginBottom: "1rem",
									marginTop: "1rem",
								}}
							>
								<IconButton
									backgroundcolorprop={colors.white}
									IconNum={2}
									buttonLabel="Actualizar"
									textcolor={colors.primaryGreen}
									hoverbackgroundcolor={colors.green}
									hovertextcolor={colors.white}
									hoverbordercolor={colors.green}
									OnClick={handleUpdate}
									fontSize="0.75rem"
								/>
							</Grid>
						)}
						<Grid
							item
							xs={12}
							sx={{
								width: "100%",
								display: "flex",
								justifyContent: "center",
							}}
						>
							<IconButton
								backgroundcolorprop={colors.white}
								IconNum={6}
								buttonLabel="Logout"
								textcolor={colors.primaryGreen}
								hoverbackgroundcolor={colors.green}
								hovertextcolor={colors.white}
								hoverbordercolor={colors.green}
								OnClick={handleLogout}
								fontSize="0.75rem"
							/>
						</Grid>
					</Box>
				</Grid>
			</Box>
		</>
	);
};

export default ClientsBar;
